import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Page furniture: the header block and section headings every screen shares.
 *
 * Centralised so heading levels stay correct — exactly one `<h1>` per page, sections at `<h2>` —
 * which is what lets a screen-reader user navigate by heading instead of reading linearly.
 */

/** Props for {@link PageHeader}. */
export interface PageHeaderProps {
  /** The page's `<h1>`. */
  title: string;
  /** One or two sentences of orientation. */
  description?: ReactNode | undefined;
  /** Right-aligned actions. */
  actions?: ReactNode | undefined;
  /** A badge or status chip shown beside the title. */
  badge?: ReactNode | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** The page title block. */
export function PageHeader({
  title,
  description,
  actions,
  badge,
  className,
}: PageHeaderProps): ReactNode {
  return (
    <header
      className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between', className)}
    >
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
          {badge}
        </div>
        {description ? (
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** Props for {@link Section}. */
export interface SectionProps {
  /** The section's `<h2>`. */
  title: string;
  /** Supporting copy. */
  description?: ReactNode | undefined;
  /** Right-aligned actions. */
  actions?: ReactNode | undefined;
  /** Section body. */
  children: ReactNode;
  /** Additional classes. */
  className?: string | undefined;
}

/** A titled section of a page. */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: SectionProps): ReactNode {
  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="space-y-0.5">
          <h2 className="text-sm font-semibold tracking-tight text-foreground">{title}</h2>
          {description ? (
            <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A labelled read-only field, used across detail panels. */
export function Field({
  label,
  children,
  hint,
  className,
}: {
  /** Field label. */
  label: string;
  /** Field value. */
  children: ReactNode;
  /** Optional explanatory note. */
  hint?: ReactNode | undefined;
  /** Additional classes. */
  className?: string | undefined;
}): ReactNode {
  // The hint lives *inside* the `<dd>`: a `<dl>`'s `<div>` wrapper may contain only `<dt>` and
  // `<dd>` elements (HTML content model, enforced by axe's `definition-list` rule), so a sibling
  // `<p>` here would make every detail panel in the app fail an accessibility audit.
  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="space-y-1 break-words text-sm text-foreground">
        <span className="block">{children}</span>
        {hint ? (
          <span className="block text-2xs leading-relaxed text-muted-foreground">{hint}</span>
        ) : null}
      </dd>
    </div>
  );
}
